import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Info, X } from 'lucide-react';
import ScorecardNavigation from './ScorecardNavigation';
import EstimatedMarker from './pilot/EstimatedMarker';
import { useTenant } from '../../contexts/TenantContext';
import {
  getPilotScorecard,
  getPilotTrend,
  getPilotPerStore,
  getPilotAdjustments,
  PilotNotConfiguredError,
  PilotView,
  PilotCompare,
  PilotScorecard,
  PilotMetric,
  PilotTrend,
  TrendSeries,
  PerStore,
  PilotAdjustments,
} from '../../services/pilotDashboardService';

// ── Token values for SVG stroke attributes ────────────────────────────────────
// Tailwind classes can't reach SVG `stroke`/`fill` attributes, so the chart
// neutrals are expressed as the bootstrapped token VALUES (coral/gold/inkMuted),
// not new one-off hexes. inkMuted at low opacity covers the export's grey family.
const C_CORAL = '#D55847';
const C_GOLD = '#E9A23B';
const C_INKMUTED = '#717182';

// ── Info popover copy (static, matches real metric semantics) ──────────────────
const INFO_CONTENT: Record<string, { title: string; body: string }> = {
  ring: {
    title: 'Ring (average transaction)',
    body: 'Pilot-total average dollar value per transaction across the pilot stores, transaction-weighted. Compared against the same stores aggregated from last year (or against the control fleet if Compare is toggled). Adjustments applied to baseline for menu price, staffing, and local competitive changes.',
  },
  ipt: {
    title: 'Items per transaction',
    body: 'Pilot-total average items per check across the pilot stores, transaction-weighted. Compared against the same stores aggregated from last year (or against the control fleet if Compare is toggled).',
  },
};

// ── Chart geometry ─────────────────────────────────────────────────────────────
const W = 460, H = 120, PAD_T = 10, PAD_B = 20, PLOT_H = H - PAD_T - PAD_B;
function tx(i: number, total: number) { return total <= 1 ? 0 : (i / (total - 1)) * W; }
function ty(v: number, yMin: number, yMax: number) { return yMax === yMin ? PAD_T + PLOT_H / 2 : PAD_T + (1 - (v - yMin) / (yMax - yMin)) * PLOT_H; }
function pts(vals: Array<number | null>, yMin: number, yMax: number) {
  return vals
    .map((v, i) => (v == null ? null : `${tx(i, vals.length).toFixed(2)},${ty(v, yMin, yMax).toFixed(2)}`))
    .filter(Boolean)
    .join(' ');
}

const SW = 90, SH = 28, SP_T = 4, SP_B = 6, SP_H = SH - SP_T - SP_B;
function sx(i: number, total: number) { return total <= 1 ? 0 : (i / (total - 1)) * SW; }
function sy(v: number, yMin: number, yMax: number) { return yMax === yMin ? SP_T + SP_H / 2 : SP_T + (1 - (v - yMin) / (yMax - yMin)) * SP_H; }
function spts(vals: Array<number | null>, yMin: number, yMax: number) {
  return vals
    .map((v, i) => (v == null ? null : `${sx(i, vals.length).toFixed(2)},${sy(v, yMin, yMax).toFixed(2)}`))
    .filter(Boolean)
    .join(' ');
}

function fmtSeries(fmt: 'currency' | 'decimal', v: number): string {
  return fmt === 'currency' ? `$${v.toFixed(2)}` : v.toFixed(2);
}

// ── SparkLine (inline 7-point chart) ────────────────────────────────────────────
function SparkLine({ metric, onHover }: { metric: PilotMetric; onHover: (txt: string | null) => void }) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const values = metric.series.map((p) => p.value);
  const fmt: 'currency' | 'decimal' = metric.value_display.startsWith('$') ? 'currency' : 'decimal';

  function handleMove(e: React.MouseEvent<SVGSVGElement>) {
    const rect = svgRef.current!.getBoundingClientRect();
    const frac = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const idx = Math.round(frac * (values.length - 1));
    setHoverIdx(idx);
    const pt = metric.series[idx];
    onHover(pt && pt.value != null ? `${pt.date}: ${fmtSeries(fmt, pt.value)}` : null);
  }
  function handleLeave() { setHoverIdx(null); onHover(null); }

  if (values.length === 0) return null;

  return (
    <svg
      ref={svgRef}
      width={SW}
      height={SH}
      viewBox={`0 0 ${SW} ${SH}`}
      className="cursor-crosshair"
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
    >
      <line x1="0" y1={SH - SP_B} x2={SW} y2={SH - SP_B} stroke={C_INKMUTED} strokeOpacity="0.3" strokeWidth="0.75" strokeDasharray="2,2" />
      <polyline fill="none" stroke={C_CORAL} strokeWidth="1.5" points={spts(values, metric.spark_min, metric.spark_max)} />
      {hoverIdx !== null && values[hoverIdx] != null && (
        <>
          <line x1={sx(hoverIdx, values.length)} x2={sx(hoverIdx, values.length)} y1="2" y2={SH - 2} stroke={C_GOLD} strokeWidth="1" />
          <circle cx={sx(hoverIdx, values.length)} cy={sy(values[hoverIdx]!, metric.spark_min, metric.spark_max)} r="2.5" fill={C_CORAL} />
        </>
      )}
    </svg>
  );
}

// ── TrendChart (28-day overlay) ──────────────────────────────────────────────────
function TrendChart({ series, dates, iterations, baseLabel }: {
  series: TrendSeries;
  dates: string[];
  iterations: PilotTrend['iterations'];
  baseLabel: string;
}) {
  const [hover, setHover] = useState<{ idx: number; x: number; y: number } | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const n = series.this_year.length;

  function handleMove(e: React.MouseEvent<HTMLDivElement>) {
    const rect = svgRef.current!.getBoundingClientRect();
    const frac = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const idx = Math.round(frac * (n - 1));
    const cur = series.this_year[idx];
    if (cur == null) { setHover(null); return; }
    const svgX = tx(idx, n);
    const svgY = ty(cur, series.y_min, series.y_max);
    setHover({ idx, x: (svgX / W) * rect.width, y: (svgY / H) * rect.height });
  }

  return (
    <div>
      <div className="flex gap-4 mb-2 flex-wrap">
        <span className="text-xs text-inkMuted flex items-center gap-1.5"><span className="inline-block w-5 h-0.5 bg-coral" />This year</span>
        <span className="text-xs text-inkMuted flex items-center gap-1.5"><span className="inline-block w-5 h-0.5 border-t border-dashed border-inkMuted/50" />{baseLabel === 'control' ? 'Control stores' : 'Last year'}</span>
        <span className="text-xs text-inkMuted flex items-center gap-1.5"><span className="inline-block w-px h-3 bg-inkMuted/40" />Iter start</span>
      </div>
      <div ref={hostRef} className="relative cursor-crosshair" onMouseMove={handleMove} onMouseLeave={() => setHover(null)}>
        <svg ref={svgRef} width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
          <line x1="0" y1={H - PAD_B} x2={W} y2={H - PAD_B} stroke={C_INKMUTED} strokeOpacity="0.18" strokeWidth="1" />
          <line x1="0" y1={H - PAD_B - 30} x2={W} y2={H - PAD_B - 30} stroke={C_INKMUTED} strokeOpacity="0.08" strokeWidth="1" />
          <line x1="0" y1={H - PAD_B - 60} x2={W} y2={H - PAD_B - 60} stroke={C_INKMUTED} strokeOpacity="0.08" strokeWidth="1" />
          {iterations.map((it) => {
            const x = tx(it.day_index, n);
            return (
              <g key={it.label}>
                <line x1={x} x2={x} y1="6" y2={H - PAD_B} stroke={C_INKMUTED} strokeOpacity="0.25" strokeWidth="1" />
                <text x={x + 3} y="14" fontSize="9" fill={C_INKMUTED} fontFamily="Nunito,sans-serif" fontWeight="500">{it.label}</text>
              </g>
            );
          })}
          <polyline fill="none" stroke={C_INKMUTED} strokeOpacity="0.5" strokeWidth="1" strokeDasharray="3,3" points={pts(series.baseline, series.y_min, series.y_max)} />
          <polyline fill="none" stroke={C_CORAL} strokeWidth="1.5" points={pts(series.this_year, series.y_min, series.y_max)} />
          {hover && series.this_year[hover.idx] != null && (
            <g>
              <line x1={tx(hover.idx, n)} x2={tx(hover.idx, n)} y1="6" y2={H - PAD_B} stroke={C_GOLD} strokeWidth="1" />
              {series.baseline[hover.idx] != null && (
                <circle cx={tx(hover.idx, n)} cy={ty(series.baseline[hover.idx]!, series.y_min, series.y_max)} r="3" fill={C_INKMUTED} fillOpacity="0.6" />
              )}
              <circle cx={tx(hover.idx, n)} cy={ty(series.this_year[hover.idx]!, series.y_min, series.y_max)} r="3.5" fill={C_CORAL} />
            </g>
          )}
        </svg>
        {hover && series.this_year[hover.idx] != null && (
          <div
            className="absolute pointer-events-none bg-white border border-softBorder shadow-md rounded-lg px-3 py-2 text-xs z-10 whitespace-nowrap"
            style={{ left: Math.min(hover.x + 12, hostRef.current ? hostRef.current.offsetWidth - 130 : hover.x + 12), top: Math.max(hover.y - 60, 4) }}
          >
            <div className="text-inkMuted mb-1">{dates[hover.idx]}</div>
            <div className="flex items-center gap-1.5 font-semibold text-coral"><span className="w-2 h-0.5 bg-coral inline-block" />{fmtSeries(series.fmt, series.this_year[hover.idx]!)}</div>
            {series.baseline[hover.idx] != null && (
              <div className="flex items-center gap-1.5 text-inkMuted"><span className="w-2 h-0.5 bg-inkMuted/50 inline-block" />{fmtSeries(series.fmt, series.baseline[hover.idx]!)} {baseLabel}</div>
            )}
          </div>
        )}
      </div>
      <div className="flex justify-between mt-1">
        <span className="text-xs text-inkMuted/60">{dates[0]?.replace(/^\w+ /, '')}</span>
        <span className="text-xs text-inkMuted/60">{dates[Math.floor(dates.length / 2)]?.replace(/^\w+ /, '')}</span>
        <span className="text-xs text-inkMuted/60">{dates[dates.length - 1]?.replace(/^\w+ /, '')}</span>
      </div>
    </div>
  );
}

// ── Metric delta + marker row ────────────────────────────────────────────────────
function DeltaRow({ metric, fmt, showBaseline }: { metric: PilotMetric; fmt: 'pct' | 'usd'; showBaseline: boolean }) {
  // No-coverage is handled at the card level (— + Awaiting POS), not here.
  // The marker keys off the COMPARISON flag, not the (real) headline value.
  const seeded = metric.baseline_placeholder;
  const deltaText = fmt === 'pct' ? metric.delta_pct_display : metric.delta_usd_display;
  const deltaColor = seeded ? 'text-preview' : 'text-success';
  return (
    <div className="text-sm font-sans flex flex-wrap items-center gap-x-1.5 gap-y-1">
      {deltaText && <span className={`font-bold ${deltaColor}`}>{deltaText}</span>}
      {showBaseline && metric.baseline_display && (
        <span className="text-inkMuted">vs {metric.baseline_display} {metric.baseline_label}</span>
      )}
      {seeded && <EstimatedMarker variant="chip" />}
    </div>
  );
}

// ── Targets band bar ────────────────────────────────────────────────────────────
// Band geometry (low_pos/high_pos/fill, all 0-100 track positions) is
// server-authoritative per ADR 0002 — rendered verbatim, not recomputed. The
// fallbacks guard a malformed payload that omits the coords (defensive only).
const BAND_TICK_FALLBACK: Record<'coral' | 'gold', { low: number; high: number }> = {
  coral: { low: 40, high: 100 }, // Ring band
  gold: { low: 80, high: 100 },  // IPT band
};

function BandBar({ metric, accent }: { metric: PilotMetric; accent: 'coral' | 'gold' }) {
  const band = metric.band;
  if (!band) return null;
  const barColor = accent === 'coral' ? 'bg-coral' : 'bg-gold';
  const stateColor = accent === 'coral' ? 'text-coral' : 'text-gold';
  const lowPos = band.low_pos ?? BAND_TICK_FALLBACK[accent].low;
  const highPos = band.high_pos ?? BAND_TICK_FALLBACK[accent].high;
  const stateLabel = band.state.replace(/_/g, ' '); // "in_band" → "in band"
  return (
    <div className="mt-3 flex items-center gap-3 flex-wrap">
      <div className="flex-none w-36 h-1.5 bg-cream-dark rounded-full relative overflow-hidden">
        <div className={`absolute left-0 top-0 bottom-0 ${barColor} rounded-full`} style={{ width: `${band.fill}%` }} />
        <div className="absolute top-[-4px] bottom-[-4px] w-px bg-ink" style={{ left: `${lowPos}%` }} />
        <div className="absolute top-[-4px] bottom-[-4px] w-px bg-ink" style={{ left: `${highPos}%` }} />
      </div>
      <span className="text-xs text-inkMuted font-sans">Band <strong className="text-ink">{band.display}</strong> · <span className={stateColor}>{stateLabel}</span></span>
      {metric.baseline_placeholder && <EstimatedMarker variant="chip" />}
    </div>
  );
}

// ── Metric card (Ring / IPT) ──────────────────────────────────────────────────────
function MetricCard({ label, metric, fmt, revealed, onInfo, sparkHint, onSparkHover, accent, side }: {
  label: string;
  metric: PilotMetric;
  fmt: 'pct' | 'usd';
  revealed: Record<string, boolean>;
  onInfo: (key: string, e: React.MouseEvent) => void;
  sparkHint: string | null;
  onSparkHover: (txt: string | null) => void;
  accent: 'coral' | 'gold';
  side: 'left' | 'right';
}) {
  const key = label.toLowerCase();
  const noCoverage = metric.value === null || metric.data_source === 'no_coverage';
  return (
    <div className={side === 'left' ? 'pr-8' : 'pl-8'}>
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs font-semibold uppercase tracking-widest text-inkMuted/70 font-sans">{label}</span>
        <button
          onClick={(e) => onInfo(key, e)}
          className="w-4 h-4 rounded-full border border-inkMuted/30 flex items-center justify-center text-inkMuted/60 hover:border-coral hover:text-coral transition-colors"
          aria-label={`About ${label}`}
        >
          <Info className="w-2.5 h-2.5" />
        </button>
      </div>

      {noCoverage ? (
        <>
          <div className="font-display text-5xl text-inkMuted/50 leading-none mb-1">—</div>
          <div className="text-sm text-inkMuted mt-1">Awaiting POS data for this store/window</div>
        </>
      ) : (
        <>
          <div className="flex items-end gap-3 mb-1">
            <span className="font-display text-5xl text-ink leading-none">{metric.value_display}</span>
            <div className="mb-1 flex flex-col gap-0.5">
              <SparkLine metric={metric} onHover={onSparkHover} />
              {sparkHint && <span className="text-[10px] text-inkMuted">{sparkHint}</span>}
            </div>
          </div>
          <DeltaRow metric={metric} fmt={fmt} showBaseline={!!revealed.baseline} />
          {revealed.targets && <BandBar metric={metric} accent={accent} />}
        </>
      )}
    </div>
  );
}

// ── Loading skeleton ────────────────────────────────────────────────────────────
function Skeleton() {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-softBorder overflow-hidden animate-pulse">
      <div className="h-12 border-b border-softBorder bg-cream/60" />
      <div className="h-14 border-b border-softBorder bg-cream/60" />
      <div className="grid grid-cols-2 gap-8 px-6 py-8">
        {[0, 1].map((i) => (
          <div key={i} className="space-y-3">
            <div className="h-3 w-16 bg-cream-dark rounded" />
            <div className="h-12 w-40 bg-cream-dark rounded" />
            <div className="h-4 w-32 bg-cream-dark rounded" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Deferred-panel loading + error states (feat-085 P2-B) ──────────────────────────
// Shown inside a revealed Trend / Per-store panel while its (deferred) request is
// in flight or after it fails — so a heavy panel reveals instantly with a skeleton
// instead of popping in late, and a panel error stays scoped to that panel.
function PanelLoading({ label }: { label: string }) {
  return (
    <div className="animate-pulse" aria-busy="true" data-testid="panel-loading">
      <div className="h-3 w-32 bg-cream-dark rounded mb-4" />
      <div className="h-24 w-full bg-cream-dark/60 rounded" />
      <span className="sr-only">Loading {label}…</span>
    </div>
  );
}

function PanelError({ message }: { message: string }) {
  return <p className="text-sm text-inkMuted" role="status" data-testid="panel-error">{message}</p>;
}

// ── Non-pilot empty state (mockup §E) ──────────────────────────────────────────────
function NoPilotConfigured() {
  return (
    <div className="rounded-2xl bg-white shadow-sm border border-softBorder p-10 text-center flex flex-col items-center gap-3">
      <div className="w-12 h-12 rounded-2xl bg-cream-dark flex items-center justify-center">
        <svg className="w-6 h-6 text-inkMuted" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3v18h18" /><path d="M18 9l-5 5-2-2-4 4" /></svg>
      </div>
      <h3 className="font-display text-lg text-ink">No pilot configured</h3>
      <p className="text-inkMuted text-sm max-w-xs">The Pilot Scorecard is set up per design-partner pilot. This tenant isn't running one yet.</p>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────────
const PILLS = [
  { key: 'baseline', label: 'Baseline' },
  { key: 'trend', label: 'Trend' },
  { key: 'annualized', label: 'Annualized' },
  { key: 'targets', label: 'Targets' },
  { key: 'bystore', label: 'Per store' },
  { key: 'adjustments', label: 'Adjustments' },
];

const PilotDashboardPage: React.FC = () => {
  const { selectedTenant } = useTenant();

  // ── Session-only view state (ADR D7) ──
  const [tab, setTab] = useState<PilotView>('cumulative');
  const [fmt, setFmt] = useState<'pct' | 'usd'>('pct');
  const [compare, setCompare] = useState<'last_year' | 'control'>('last_year');
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});
  const [infoKey, setInfoKey] = useState<string | null>(null);
  const [infoPos, setInfoPos] = useState({ left: 0, top: 0 });
  const [modalOpen, setModalOpen] = useState(false);
  const [sparkHint, setSparkHint] = useState<Record<string, string | null>>({ ring: null, ipt: null });

  // ── Data state ──
  const [scorecard, setScorecard] = useState<PilotScorecard | null>(null);
  const [trend, setTrend] = useState<PilotTrend | null>(null);
  const [perStore, setPerStore] = useState<PerStore | null>(null);
  const [adjustments, setAdjustments] = useState<PilotAdjustments | null>(null);
  const [loading, setLoading] = useState(true);
  const [notConfigured, setNotConfigured] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Progressive-load state (feat-085 P2-B) ──
  // The heavy trend + per-store panels are DEFERRED: they don't fetch until their
  // pill is first revealed, so the initial load fires only the scorecard headline
  // (+ cheap adjustments) instead of bursting all four endpoints. Each deferred
  // panel carries its own error state so a failure shows in-panel, never blanking
  // the headline. The `requested` latches stay set across collapse/expand so the
  // panel doesn't refetch on every toggle — only on a genuine compare change.
  const [trendRequested, setTrendRequested] = useState(false);
  const [perStoreRequested, setPerStoreRequested] = useState(false);
  const [trendError, setTrendError] = useState(false);
  const [perStoreError, setPerStoreError] = useState(false);

  const compareParam: PilotCompare = compare;

  // Scorecard: (re)fetch on tenant / view / compare change.
  useEffect(() => {
    if (!selectedTenant) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setNotConfigured(false);
    getPilotScorecard(selectedTenant, tab, compareParam)
      .then((sc) => { if (!cancelled) setScorecard(sc); })
      .catch((e) => {
        if (cancelled) return;
        if (e instanceof PilotNotConfiguredError) setNotConfigured(true);
        else setError('Could not load the pilot scorecard.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selectedTenant, tab, compareParam]);

  // Latch the deferred panels "requested" the first time they're revealed. The
  // latch persists across collapse/expand so re-opening a panel doesn't refetch;
  // only a tenant/compare change re-fires the request below.
  useEffect(() => { if (revealed.trend) setTrendRequested(true); }, [revealed.trend]);
  useEffect(() => { if (revealed.bystore) setPerStoreRequested(true); }, [revealed.bystore]);

  // Trend: DEFERRED (P2-B) — fetch only once requested; (re)fetch on tenant /
  // compare change. View-independent. A failure surfaces in-panel (trendError),
  // it does not break the headline.
  useEffect(() => {
    if (!selectedTenant || !trendRequested) return;
    let cancelled = false;
    setTrendError(false);
    getPilotTrend(selectedTenant, compareParam)
      .then((t) => { if (!cancelled) setTrend(t); })
      .catch(() => { if (!cancelled) setTrendError(true); });
    return () => { cancelled = true; };
  }, [selectedTenant, compareParam, trendRequested]);

  // Per-store: DEFERRED (P2-B) — fetch only once requested. MUST depend on
  // compareParam: the control-fleet footer row (ADR 0007 D5) only comes back under
  // compare=control, so toggling Compare while open must re-request the table (the
  // feat-059 §1 bug: per-store silently kept the last_year rows).
  useEffect(() => {
    if (!selectedTenant || !perStoreRequested) return;
    let cancelled = false;
    setPerStoreError(false);
    getPilotPerStore(selectedTenant, compareParam)
      .then((p) => { if (!cancelled) setPerStore(p); })
      .catch(() => { if (!cancelled) setPerStoreError(true); });
    return () => { cancelled = true; };
  }, [selectedTenant, compareParam, perStoreRequested]);

  // Adjustments: fetch once per tenant (view- and compare-independent in b1).
  useEffect(() => {
    if (!selectedTenant) return;
    let cancelled = false;
    getPilotAdjustments(selectedTenant).then((a) => { if (!cancelled) setAdjustments(a); }).catch(() => {});
    return () => { cancelled = true; };
  }, [selectedTenant]);

  const toggleReveal = useCallback((key: string) => {
    setRevealed((r) => ({ ...r, [key]: !r[key] }));
  }, []);

  const toggleInfo = useCallback((key: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setInfoKey((cur) => {
      if (cur === key) return null;
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      setInfoPos({ left: rect.right + 8, top: rect.top - 4 });
      return key;
    });
  }, []);

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { setInfoKey(null); setModalOpen(false); }
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, []);

  return (
    <div className="min-h-screen bg-cream" onClick={() => setInfoKey(null)}>
      <ScorecardNavigation />

      <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
        {/* Page title */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="font-display text-2xl text-ink">Pilot Scorecard</h1>
            {scorecard && !notConfigured && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-coral text-white text-xs font-semibold tracking-wide">{scorecard.header.iteration}</span>
            )}
          </div>
          {scorecard && !notConfigured && (
            <p className="text-sm text-inkMuted font-sans">
              Pilot total · {scorecard.header.store_count} store{scorecard.header.store_count === 1 ? '' : 's'} combined · transaction-weighted
              <span className="mx-2 text-inkMuted/40">·</span>
              <span
                className="border-b border-dotted border-inkMuted/40 cursor-help"
                title={scorecard.header.updated_at_display ? `Last updated ${scorecard.header.updated_at_display}` : undefined}
              >
                Day {scorecard.header.day_of} of {scorecard.header.pilot_length}
              </span>
            </p>
          )}
        </div>

        {notConfigured ? (
          <NoPilotConfigured />
        ) : loading && !scorecard ? (
          <Skeleton />
        ) : error && !scorecard ? (
          <div className="rounded-2xl bg-white shadow-sm border border-softBorder p-10 text-center">
            <p className="text-inkMuted text-sm">{error}</p>
          </div>
        ) : scorecard ? (
          <div className="bg-white rounded-2xl shadow-sm border border-softBorder overflow-hidden">
            {/* Tabs */}
            <div className="flex border-b border-softBorder px-6">
              {[{ id: 'cumulative', label: 'Cumulative (pilot)' }, { id: 'today', label: 'Today (intra-day)' }].map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id as PilotView)}
                  className={`py-3.5 mr-7 text-sm font-sans font-semibold border-b-2 transition-colors ${tab === t.id ? 'border-coral text-coral' : 'border-transparent text-inkMuted hover:text-ink'}`}
                >{t.label}</button>
              ))}
            </div>

            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 px-6 py-4 border-b border-softBorder bg-cream/60">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-inkMuted/60 font-sans">View</span>
                <div className="flex bg-cream-dark rounded-lg p-0.5 border border-softBorder">
                  {[{ v: 'pct', l: '%' }, { v: 'usd', l: '$' }].map((o) => (
                    <button
                      key={o.v}
                      onClick={() => setFmt(o.v as 'pct' | 'usd')}
                      className={`px-3 py-1 rounded-md text-xs font-semibold font-sans transition-all ${fmt === o.v ? 'bg-white shadow-sm text-coral' : 'text-inkMuted'}`}
                    >{o.l}</button>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-inkMuted/60 font-sans">Show</span>
                {PILLS.map((p) => (
                  <button
                    key={p.key}
                    onClick={() => toggleReveal(p.key)}
                    aria-pressed={!!revealed[p.key]}
                    className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold font-sans border transition-all ${revealed[p.key] ? 'bg-coral text-white border-coral' : 'border-inkMuted/30 text-inkMuted hover:border-coral hover:text-coral'}`}
                  >
                    <span className="text-sm leading-none">{revealed[p.key] ? '−' : '+'}</span>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Headline metrics */}
            <div className="grid grid-cols-[1fr_1px_1fr] px-6 py-6">
              <MetricCard label="Ring" metric={scorecard.ring} fmt={fmt} revealed={revealed} onInfo={toggleInfo} sparkHint={sparkHint.ring} onSparkHover={(txt) => setSparkHint((h) => ({ ...h, ring: txt }))} accent="coral" side="left" />
              <div className="bg-softBorder" />
              <MetricCard label="IPT" metric={scorecard.ipt} fmt={fmt} revealed={revealed} onInfo={toggleInfo} sparkHint={sparkHint.ipt} onSparkHover={(txt) => setSparkHint((h) => ({ ...h, ipt: txt }))} accent="gold" side="right" />
            </div>

            {/* Annualized */}
            {revealed.annualized && (
              <div className="mx-6 mb-4 px-5 py-4 bg-gradient-to-r from-gold-light/50 to-gold-light/20 border border-gold/30 rounded-xl">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm font-sans text-ink">Annualized lift at measured rate across {scorecard.header.store_count} pilot store{scorecard.header.store_count === 1 ? '' : 's'}: <strong className="text-coral font-display text-lg">{scorecard.annualized.value_display}</strong></p>
                  {scorecard.annualized.placeholder && <EstimatedMarker variant="chip" />}
                </div>
                <p className="text-xs text-inkMuted mt-1">Combined Ring + IPT impact, measured rate only, no fleet-wide projection.</p>
              </div>
            )}

            {/* Trend (deferred — fetched on first reveal, P2-B) */}
            {revealed.trend && (
              <div className="px-6 py-5 border-t border-softBorder">
                {trend ? (
                <>
                <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-xs font-semibold uppercase tracking-widest text-inkMuted/70 font-sans">Trend over pilot</h3>
                    {trend.placeholder && <EstimatedMarker variant="panel" label="Estimated" />}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-inkMuted/60 font-sans">Compare</span>
                    <div className="flex bg-cream-dark rounded-lg p-0.5 border border-softBorder">
                      {[{ v: 'last_year', l: 'Last year' }, { v: 'control', l: 'Control' }].map((o) => (
                        <button
                          key={o.v}
                          onClick={() => setCompare(o.v as 'last_year' | 'control')}
                          className={`px-3 py-1 rounded-md text-xs font-semibold font-sans transition-all ${compare === o.v ? 'bg-white shadow-sm text-coral' : 'text-inkMuted'}`}
                        >{o.l}</button>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {(['ring', 'ipt'] as const).map((m) => (
                    <div key={m}>
                      <p className="text-sm font-semibold text-ink mb-1 font-sans">{m === 'ring' ? 'Ring' : 'IPT'}</p>
                      <TrendChart series={trend[m]} dates={trend.dates} iterations={trend.iterations} baseLabel={trend.baseline_label} />
                    </div>
                  ))}
                </div>
                </>
                ) : trendError ? (
                  <PanelError message="Couldn’t load the trend chart." />
                ) : (
                  <PanelLoading label="the trend chart" />
                )}
              </div>
            )}

            {/* Per store (deferred — fetched on first reveal, P2-B) */}
            {revealed.bystore && (
              <div className="px-6 py-5 border-t border-softBorder">
                {perStore ? (
                <>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <h3 className="text-xs font-semibold uppercase tracking-widest text-inkMuted/70 font-sans">Per store</h3>
                  {perStore.placeholder && <EstimatedMarker variant="panel" label="Some comparisons estimated" />}
                </div>
                <p className="text-xs text-inkMuted mb-4">Each store vs its own day-of-week-matched last-year baseline.</p>
                <table className="w-full text-sm font-sans">
                  <thead>
                    <tr className="border-b border-softBorder">
                      {['Store', 'Ring', 'vs baseline', 'IPT', 'vs baseline'].map((h, i) => (
                        <th key={i} className={`py-2 text-[10px] font-semibold uppercase tracking-wide text-inkMuted/60 ${i === 0 ? 'text-left' : 'text-right'}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {perStore.rows.map((row) => {
                      const thin = row.data_source === 'no_coverage';
                      return (
                        <tr key={row.store} className="border-b border-cream-dark">
                          <td className={`py-2.5 ${thin ? 'text-inkMuted' : 'text-ink'}`}>{row.store}</td>
                          <td className={`py-2.5 text-right font-semibold ${thin ? 'text-inkMuted/60' : 'text-ink'}`}>{row.ring_display}</td>
                          <td className="py-2.5 text-right font-semibold">
                            {thin ? (
                              <span className="text-inkMuted text-xs">awaiting POS</span>
                            ) : row.ring_delta_display == null ? (
                              // feat-059: the control-fleet row IS the baseline, so it
                              // carries no "vs baseline" delta — render blank, not an
                              // empty colored span.
                              <span className="text-inkMuted/40">—</span>
                            ) : (
                              <span className={row.placeholder ? 'text-preview' : 'text-success'}>
                                {row.ring_delta_display}
                                {row.placeholder && <EstimatedMarker variant="dot" className="ml-1" />}
                              </span>
                            )}
                          </td>
                          <td className={`py-2.5 text-right font-semibold ${thin ? 'text-inkMuted/60' : 'text-ink'}`}>{row.ipt_display}</td>
                          <td className="py-2.5 text-right font-semibold">
                            {thin ? (
                              <span className="text-inkMuted text-xs">—</span>
                            ) : row.ipt_delta_display == null ? (
                              <span className="text-inkMuted/40">—</span>
                            ) : (
                              <span className={row.placeholder ? 'text-preview' : 'text-success'}>
                                {row.ipt_delta_display}
                                {row.placeholder && <EstimatedMarker variant="dot" className="ml-1" />}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    <tr>
                      <td className="py-2.5 font-bold text-ink">{perStore.total.store}</td>
                      <td className="py-2.5 text-right font-bold text-ink">{perStore.total.ring_display}</td>
                      <td className={`py-2.5 text-right font-bold ${perStore.total.placeholder ? 'text-preview' : 'text-success'}`}>{perStore.total.ring_delta_display}</td>
                      <td className="py-2.5 text-right font-bold text-ink">{perStore.total.ipt_display}</td>
                      <td className={`py-2.5 text-right font-bold ${perStore.total.placeholder ? 'text-preview' : 'text-success'}`}>{perStore.total.ipt_delta_display}</td>
                    </tr>
                  </tbody>
                </table>
                </>
                ) : perStoreError ? (
                  <PanelError message="Couldn’t load the per-store table." />
                ) : (
                  <PanelLoading label="the per-store table" />
                )}
              </div>
            )}

            {/* Adjustments (read-only display in b1) */}
            {revealed.adjustments && adjustments && (
              <div className="px-6 py-5 border-t border-softBorder">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <h3 className="text-xs font-semibold uppercase tracking-widest text-inkMuted/70 font-sans">Adjustments applied to baseline</h3>
                  {adjustments.placeholder && <EstimatedMarker variant="panel" label="Sample — estimated" />}
                </div>
                <p className="text-sm font-sans mb-4 text-ink"><strong>{adjustments.net_display} net baseline adjustment.</strong> Methodology and per-item rationale below.</p>
                <div className="space-y-3">
                  {adjustments.rows.map((row) => (
                    <div key={row.id} className="flex items-start gap-4 py-2.5 border-b border-cream-dark last:border-0 text-sm font-sans">
                      <div className="flex-1">
                        <p className="text-ink">{row.description}</p>
                        {row.note && <p className="text-inkMuted text-xs mt-0.5">{row.note}</p>}
                      </div>
                      <span className={`text-xs font-semibold whitespace-nowrap mt-0.5 ${row.flagged ? 'text-gold' : 'text-ink'}`}>{row.impact_display}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="px-6 py-4 border-t border-softBorder bg-cream/60">
              <button onClick={() => setModalOpen(true)} className="text-xs text-coral underline underline-offset-2 font-sans hover:opacity-90 transition-opacity">view methodology</button>
            </div>
          </div>
        ) : null}
      </div>

      {/* Info popover */}
      {infoKey && INFO_CONTENT[infoKey] && (
        <div
          className="fixed z-50 bg-white border border-softBorder shadow-lg rounded-xl p-4 max-w-xs text-sm font-sans"
          style={{ left: infoPos.left, top: infoPos.top }}
          onClick={(e) => e.stopPropagation()}
        >
          <p className="font-semibold text-ink mb-1 text-xs uppercase tracking-wide">{INFO_CONTENT[infoKey].title}</p>
          <p className="text-inkMuted text-xs leading-relaxed">{INFO_CONTENT[infoKey].body}</p>
        </div>
      )}

      {/* Methodology modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center pt-20 px-4 overflow-y-auto" onClick={() => setModalOpen(false)}>
          <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full p-8 relative mb-8 font-sans" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setModalOpen(false)} className="absolute top-4 right-4 text-inkMuted hover:text-ink transition-colors" aria-label="Close">
              <X className="w-5 h-5" />
            </button>
            <h2 className="font-display text-xl text-ink mb-4">How we measure</h2>

            {/* Estimated caveat (ADR D4 / mockup §F) — carried until the POS backfill lands */}
            <div className="rounded-lg p-3 mb-4 flex items-start gap-2 bg-preview-light border border-preview-border">
              <Info className="w-4 h-4 mt-0.5 shrink-0 text-preview" strokeWidth={2.2} />
              <p className="text-xs text-preview leading-relaxed">
                <strong>Some figures are estimated.</strong> Ring and items-per-transaction are measured from your live POS. Baseline ("vs last year"), target bands, and annualized lift are <strong>seeded previews</strong> until your historical POS data is imported — they're labeled <span className="font-semibold">Estimated</span> wherever they appear.
              </p>
            </div>

            <div className="space-y-4 text-sm text-inkMuted leading-relaxed">
              <p>The scorecard offers two comparisons via the Compare toggle: each pilot store against ITSELF in the same period last year (aligned by day-of-week), or the pilot against a set of paired control stores over the same window.</p>
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-coral mb-1">Baseline (last year)</h3>
                <p>Built from prior-year POS data. Headline numbers use a transaction-weighted aggregate across the pilot stores from last year. Per-store breakdowns compare each store to its own prior-year baseline.</p>
                <p className="mt-2 text-xs text-inkMuted/90">Early in the pilot, a cumulative window compares N days of (still-sparse) live POS against N days of full-coverage prior-year POS — the per-transaction averages are valid, and the volume gap is expected and closes as live POS accrues.</p>
              </div>
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-coral mb-1">Control stores</h3>
                <p>Manually paired by statistician (2 stores). Transaction-weighted Ring and IPT computed from raw POS orders over the same date window as the pilot.</p>
                <p className="mt-2 text-xs text-inkMuted/90">Control comparison uses the same calendar window as the pilot (contemporaneous, not prior-year). Holiday/promo differences between pilot and control stores are not normalized in this version.</p>
              </div>
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-coral mb-1">Trend chart</h3>
                <p>Daily values for the pilot period (solid coral line) plotted against the day-of-week-matched baseline (dashed). Vertical gray lines mark iteration starts.</p>
              </div>
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-coral mb-1">Targets</h3>
                <p>Ring target band: +2% to +5%. IPT target band: +1.2 to +1.5 items. The Targets pill reveals progress bars built on these yardsticks.</p>
              </div>
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-coral mb-1">Adjustments</h3>
                <p>When something changes that would distort a fair comparison (menu price increase, staffing change, competitor opening), we adjust the baseline so the delta reflects actual operational change.</p>
              </div>
              <div>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-coral mb-1">Annualized lift</h3>
                <p>Combined Ring + IPT impact projected to 12 months at the measured rate across the pilot stores. NOT projected to the full fleet.</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PilotDashboardPage;
